/* File: src\components\journal\EditJournalEntryModal.js */
import React, { useState, useEffect, useRef } from 'react'; // Added useRef
import TagInput from './TagInput';
import { useProfile } from '../../contexts/ProfileContext';

const EditJournalEntryModal = ({ entry, onSave, onCancel }) => {
  const { activeProfile } = useProfile();
  const [editedContent, setEditedContent] = useState('');
  const [editedTags, setEditedTags] = useState([]);
  const [tagInputValue, setTagInputValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [animationState, setAnimationState] = useState('exited'); // Start exited
  const prevEntryIdRef = useRef(null); // Ref to track the previous entry ID

  // Effect to handle modal opening/closing and data population
  useEffect(() => {
    if (entry) { // Modal is intended to be open or opening
      // Populate form only if the entry ID has changed or modal was previously exited
      if (entry.id !== prevEntryIdRef.current || animationState === 'exited') {
        setEditedContent(entry.content || '');
        setEditedTags(Array.isArray(entry.tags) ? entry.tags : []);
        setTagInputValue('');
        setError('');
        prevEntryIdRef.current = entry.id; // Store the current ID
      }

      // Start entering animation if not already entered/entering
      if (animationState === 'exited') {
        setAnimationState('entering');
        const enterTimeout = setTimeout(() => {
          setAnimationState('entered');
        }, 50); // Short delay to allow initial render before animating
        return () => clearTimeout(enterTimeout); // Cleanup enter timeout
      } else if (animationState === 'entering') {
         // If already entering, ensure it completes
         const enteredTimeout = setTimeout(() => {
             // Check if still meant to be open before setting 'entered'
             if (entry) {
                 setAnimationState('entered');
             }
         }, 300); // Animation duration
         return () => clearTimeout(enteredTimeout);
      }

    } else { // Modal is intended to be closed
      // Trigger exit animation only if the modal was fully entered/visible
      if (animationState === 'entered' || animationState === 'entering') {
        setAnimationState('exiting');
        const exitTimeout = setTimeout(() => {
          setAnimationState('exited');
          // Reset form state *after* animation completes
          setEditedContent('');
          setEditedTags([]);
          setTagInputValue('');
          setError('');
          prevEntryIdRef.current = null; // Clear previous ID
        }, 300); // Animation duration matches fade-out/scale-out

        return () => clearTimeout(exitTimeout); // Cleanup exit timeout
      } else if (animationState !== 'exited') {
        // If close is triggered before fully entered or during exit, force exited state
        setAnimationState('exited');
        setEditedContent(''); setEditedTags([]); setTagInputValue(''); setError('');
        prevEntryIdRef.current = null;
      }
    }
  // Depend only on the 'entry' prop itself.
  // The logic inside handles whether to update state based on entry.id vs prevEntryIdRef.current
  }, [entry, animationState]); // Keep animationState dependency for exit logic

  // Handler for confirmed tags
  const handleTagsChange = (newTags) => {
    setEditedTags(newTags);
  };

  // Handler for tag input value
  const handleTagInputChange = (value) => {
    setTagInputValue(value);
  };

  // Save handler
  const handleSave = async (e) => {
    e.preventDefault();
    // Double check entry exists before saving
    if (!entry || !entry.id || !editedContent.trim()) return;

    setIsSaving(true);
    setError('');

    // Process unconfirmed tag if present
    const finalTags = [...editedTags];
    const unconfirmedTag = tagInputValue.trim();

    // Sanitize and add the unconfirmed tag if valid and not already present
    if (unconfirmedTag) {
        let formattedTag = unconfirmedTag;
        if (!formattedTag.startsWith('#')) { formattedTag = `#${formattedTag}`; }
        if (formattedTag.length > 25) { formattedTag = formattedTag.substring(0, 25); }
        if (formattedTag !== '#' && !finalTags.some(t => t.toLowerCase() === formattedTag.toLowerCase())) {
            finalTags.push(formattedTag);
        }
    }

    try {
      // Prepare the updated data
      const updatedData = {
        content: editedContent.trim(),
        // Ensure tags are sorted and unique before saving
        tags: [...new Set(finalTags)].sort(),
      };

      // Call parent save function (which should handle Firestore update)
      const success = await onSave(entry.id, updatedData);

      if (success) {
         handleClose(); // Trigger close animation on successful save
      } else {
         // If parent's onSave returns false, assume an error occurred there
         // (e.g., Firestore error handled by parent's catch block)
         setError("Failed to save changes. Please try again.");
      }

    } catch (error) {
      // Catch errors specifically from the onSave promise itself
      console.error("Error during parent onSave call:", error);
      setError("An unexpected error occurred while saving.");
    } finally {
      setIsSaving(false);
    }
  };

  // Close handler with animation trigger
  const handleClose = () => {
    // Start the closing animation by calling the parent's cancel function,
    // which should set the 'entry' prop to null, triggering the useEffect hook's exit logic.
    onCancel();
  };

  // Don't render if the modal is fully closed/exited
  // Important: Check !entry as well to handle initial state before opening
  if (animationState === 'exited' && !entry) {
    return null;
  }

  return (
    // Modal structure using animationState for classes
    <div
      className={`fixed inset-0 z-30 flex items-center justify-center p-4
                  ${animationState === 'entering' ? 'animate-fade-in' : ''}
                  ${animationState === 'exiting' ? 'animate-fade-out' : ''}
                  ${animationState === 'exited' ? 'opacity-0 pointer-events-none' : 'opacity-100'}
                  `}
      aria-modal="true"
      role="dialog"
      style={{ transition: 'opacity 0.3s ease-out' }} // Add transition for opacity fade
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
        onClick={isSaving ? undefined : handleClose} // Use the modified handleClose
      ></div>

      {/* Modal Content */}
      <div
        className={`bg-white rounded-xl shadow-xl w-full max-w-2xl transform transition-all duration-300 ease-out
                    ${animationState === 'entering' ? 'animate-scale-in' : ''}
                    ${animationState === 'exiting' ? 'animate-scale-out' : ''}
                    ${animationState === 'entered' ? 'scale-100 opacity-100' : ''}
                    ${/* Do not hide immediately on exit, wait for opacity transition */''}
                    `}
        onClick={e => e.stopPropagation()}
      >
          {/* Modal Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center">
              {/* Icon and Title */}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Journal Entry
            </h2>
            <button
              onClick={handleClose} // Use the modified handleClose
              disabled={isSaving}
              className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-full p-1 disabled:opacity-50"
              aria-label="Close"
            >
              {/* Close Icon */}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Modal Body (Form) */}
          <form onSubmit={handleSave}>
            <div className="p-6">
              {/* Content Textarea */}
              <div className="mb-4">
                <label htmlFor="editJournalContent" className="block text-sm font-medium text-gray-700 mb-1"> Content </label>
                <textarea
                  id="editJournalContent" value={editedContent} onChange={(e) => setEditedContent(e.target.value)}
                  rows="5" required disabled={isSaving}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 shadow-sm disabled:bg-gray-50 disabled:opacity-70"
                  placeholder="Your journal entry content..."
                />
              </div>

              {/* Tags Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <TagInput
                  tags={editedTags} availableTags={activeProfile?.journalTags || []} onChange={handleTagsChange}
                  inputValue={tagInputValue} onInputChange={handleTagInputChange} disabled={isSaving}
                />
                <p className="text-xs text-gray-500 mt-1">Enter tags separated by comma or Enter.</p>
              </div>

              {/* Error Message */}
              {error && <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm"><p>{error}</p></div>}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
              {/* Entry Type Display */}
              <div className="text-xs text-gray-500">
                {entry?.type === 'manual' ? 'Manual Entry' :
                 entry?.type === 'session_summary' ? 'Session Summary' :
                 entry?.type === 'chart_snapshot' ? 'Chart Snapshot' : 'Entry'}
              </div>
              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button type="button" onClick={handleClose} disabled={isSaving} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm font-medium disabled:opacity-50"> Cancel </button>
                <button type="submit" disabled={isSaving || !editedContent?.trim()} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-sm font-medium shadow-sm disabled:opacity-50 disabled:bg-indigo-400">
                  {isSaving ? ( <span className="flex items-center"> <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"> <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle> <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path> </svg> Saving... </span> ) : 'Save Changes'}
                </button>
              </div>
            </div>
          </form>
      </div> {/* End Modal Content */}

      {/* Minimal CSS for animations (included directly) */}
      <style jsx global>{`
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fade-out { from { opacity: 1; } to { opacity: 0; } }
        @keyframes scale-in { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes scale-out { from { transform: scale(1); opacity: 1; } to { transform: scale(0.95); opacity: 0; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        .animate-fade-out { animation: fade-out 0.3s ease-out forwards; }
        .animate-scale-in { animation: scale-in 0.3s ease-out forwards; }
        .animate-scale-out { animation: scale-out 0.3s ease-out forwards; }
      `}</style>

    </div> // End Modal Overlay
  );
};

export default EditJournalEntryModal;